import { cn } from '@invent/wl-ui-kit-next';
import { Textarea } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

/**
 * A text area as tall as its text (SR-525, D6): it grows while typed into and shrinks back, from
 * `minRows` up, so a long answer is read whole and a short one takes a line — the interview sheet's
 * fixed boxes hid most of a pasted section behind their own scroll bar. Measured after each value
 * and on a change of width; CSS `field-sizing: content` would do it without a script, but Safari and
 * Firefox ignore it.
 */
export const AutosizeTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentPropsWithoutRef<typeof Textarea> & { minRows?: number }
>(({ minRows = 2, className, value, ...props }, forwarded) => {
  const own = React.useRef<HTMLTextAreaElement>(null);
  React.useImperativeHandle(forwarded, () => own.current!);

  const fit = React.useCallback(() => {
    const box = own.current;
    if (!box) return;
    box.style.height = 'auto';
    box.style.height = `${box.scrollHeight + box.offsetHeight - box.clientHeight}px`;
  }, []);

  React.useLayoutEffect(fit, [fit, value]);
  React.useEffect(() => {
    const box = own.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    let width = box.clientWidth;
    const watch = new ResizeObserver(() => {
      if (box.clientWidth === width) return;
      width = box.clientWidth;
      fit();
    });
    watch.observe(box);
    return () => watch.disconnect();
  }, [fit]);

  return (
    <Textarea
      ref={own}
      rows={minRows}
      value={value}
      className={cn('min-h-0 resize-none overflow-hidden', className)}
      {...props}
    />
  );
});
AutosizeTextarea.displayName = 'AutosizeTextarea';
